/**
 * Shared styled components for IELTS test components
 * Used by ListeningTestUI and ListeningTestUI2
 */
import { styled } from '@mui/material/styles';
import { Box, Paper, Typography, TextField, Button, IconButton } from '@mui/material';
import { themeColors } from './theme';

// Container components
export const MainContainer = styled(Box)(({ theme, variant }) => ({
  width: '100%',
  ...(variant === 'test2' ? {
    minHeight: '100%',
    alignItems: 'flex-start',
  } : {
    flex: 1,
    alignItems: 'center',
    minHeight: 0,
  }),
  display: 'flex',
  flexDirection: 'column',
  overflowX: 'hidden',
  margin: 0,
  padding: 0,
  backgroundColor: themeColors.background,
}));

export const ContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  padding: 0,
  margin: 0,
}));

export const QuestionsContainer = styled(Paper)(({ theme }) => ({
  padding: '20px',
  height: '100%',
  minHeight: 0,
  overflowY: 'auto',
  borderRadius: '8px',
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
  marginTop: 0,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: themeColors.scrollbarTrack,
    borderRadius: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: themeColors.scrollbarThumb,
    borderRadius: '8px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: themeColors.scrollbarThumbHover,
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${themeColors.scrollbarThumb} ${themeColors.scrollbarTrack}`,
  position: 'relative',
  zIndex: 1,
}));

export const QuestionsPaper = styled(Paper)(({ theme }) => ({
  flex: 1,
  padding: '24px',
  borderRadius: '12px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('md')]: {
    padding: '16px',
  },
}));

export const AudioControlsPaper = styled(Paper)(({ theme }) => ({
  width: '300px',
  padding: '24px',
  borderRadius: '12px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    padding: '16px',
  },
}));

// Audio player components
export const AudioPlayerBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 16px',
  backgroundColor: themeColors.white,
  borderBottom: `1px solid ${themeColors.border}`,
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '6px 12px',
  },
}));

export const AudioControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flex: '0 0 auto',
}));

export const AudioProgressContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  margin: '0 16px',
  [theme.breakpoints.down('sm')]: {
    margin: '0 8px',
  },
}));

export const AudioControlButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: themeColors.primary,
  color: themeColors.white,
  '&:hover': {
    backgroundColor: themeColors.primaryHover,
  },
  '&.Mui-disabled': {
    backgroundColor: themeColors.border,
    color: themeColors.textSecondary,
  },
}));

// Typography components
export const QuestionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 600,
  marginBottom: '16px',
  color: themeColors.text,
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
  },
}));

export const QuestionText = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  marginBottom: '12px',
  color: themeColors.text,
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
  },
}));

// Input components
export const AnswerField = styled(TextField)(({ theme }) => ({
  marginBottom: '8px',
  '& .MuiOutlinedInput-root': {
    height: '36px',
    '&.Mui-focused fieldset': {
      borderColor: themeColors.primary,
    },
  },
  '& .MuiInputBase-input': {
    padding: '8px 10px',
  },
}));

// Button components
export const NavButton = styled(Button)(({ theme }) => ({
  backgroundColor: themeColors.primary,
  color: themeColors.white,
  '&:hover': {
    backgroundColor: themeColors.primaryHover,
  },
  '&.Mui-disabled': {
    backgroundColor: themeColors.border,
    color: themeColors.textSecondary,
  },
}));

export const NavigationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '24px',
}));

// Progress bar
export const ProgressBar = styled(Box)(({ theme, value }) => ({
  width: '100%',
  height: '8px',
  backgroundColor: themeColors.border,
  borderRadius: '4px',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: `${value}%`,
    backgroundColor: themeColors.primary,
    borderRadius: '4px',
    transition: 'width 0.1s linear',
  },
}));

// Option circle for MCQ
export const OptionCircle = styled(Box)(({ theme, selected }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: selected ? themeColors.primaryLight : '#F5F5F5',
  color: selected ? themeColors.primary : themeColors.text,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 18,
  border: selected ? `2px solid ${themeColors.primary}` : `2px solid ${themeColors.border}`,
  marginRight: 12,
  transition: 'all 0.2s',
}));

// Navigation components (for ListeningTestUI)
export const BottomNavigation = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  width: '100%',
  backgroundColor: themeColors.white,
  borderTop: `1px solid ${themeColors.border}`,
  zIndex: 1000,
  padding: '8px 0',
  boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.05)',
}));

export const PartTab = styled(Box)(({ theme, active }) => ({
  padding: '12px 16px',
  borderRadius: '8px',
  backgroundColor: active ? themeColors.primary : themeColors.backgroundAlt,
  color: active ? themeColors.white : themeColors.textSecondary,
  fontWeight: active ? 600 : 400,
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: active ? themeColors.primaryHover : themeColors.border,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 12px',
    fontSize: '14px',
  },
}));

export const PartNavItem = styled(Box)(({ theme, active }) => ({
  flex: 1,
  textAlign: 'center',
  padding: '8px 4px',
  backgroundColor: active ? themeColors.primaryLighter : themeColors.white,
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  margin: '0 4px',
  border: active ? `1px solid ${themeColors.primaryLight}` : `1px solid #EEEEEE`,
  '&:hover': {
    backgroundColor: active ? themeColors.primaryLighter : '#f5f5f5',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '6px 2px',
  },
}));

export const ActivePartHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%',
  padding: '0 4px',
}));

export const QuestionNumberButton = styled(Button)(({ theme, active, answered }) => ({
  minWidth: '36px',
  height: '36px',
  padding: '0',
  borderRadius: '50%',
  backgroundColor: active ? themeColors.primary : answered ? themeColors.primaryLight : themeColors.backgroundAlt,
  color: active ? themeColors.white : answered ? themeColors.primary : themeColors.textSecondary,
  fontWeight: 500,
  '&:hover': {
    backgroundColor: active ? themeColors.primaryHover : answered ? '#D6C6FF' : themeColors.border,
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '32px',
    height: '32px',
    fontSize: '12px',
  },
}));

export const QuestionNumberIndicator = styled(Box)(({ theme, active, answered, skipped }) => ({
  width: '28px',
  height: '28px',
  borderRadius: '50%',
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '0 4px',
  backgroundColor: active ? themeColors.primary : skipped ? themeColors.errorRed : answered ? themeColors.primaryLight : themeColors.backgroundAlt,
  color: active ? themeColors.white : skipped ? themeColors.white : answered ? themeColors.primary : themeColors.textSecondary,
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: active ? themeColors.primaryHover : skipped ? themeColors.error : answered ? '#D6C6FF' : themeColors.border,
  },
  [theme.breakpoints.down('sm')]: {
    width: '24px',
    height: '24px',
    fontSize: '12px',
    margin: '0 2px',
  },
}));

export const QuestionIndicatorsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  overflowX: 'auto',
  justifyContent: 'flex-start',
  padding: '8px 4px',
  '&::-webkit-scrollbar': {
    height: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: themeColors.scrollbarTrack,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: themeColors.scrollbarThumb,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: themeColors.scrollbarThumbHover,
  },
}));

export const QuestionContainer = styled(Box)(({ theme }) => ({
  marginBottom: '24px',
  padding: '16px',
  borderRadius: '8px',
  border: `1px solid ${themeColors.border}`,
  backgroundColor: themeColors.primaryLightest,
  '&:hover': {
    borderColor: themeColors.primary,
    backgroundColor: themeColors.primaryLighter,
  },
}));

// Header components (for ListeningTestUI2)
export const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 20px',
  backgroundColor: themeColors.white,
  borderBottom: `1px solid ${themeColors.border}`,
  position: 'sticky',
  top: 0,
  zIndex: 1000,
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
  },
}));

export const TimerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: themeColors.text,
  fontWeight: 'medium',
  '& .MuiSvgIcon-root': {
    color: themeColors.primary,
    marginRight: '8px',
  },
}));

